"use client"

import { AuthGuard } from '@/components/auth-guard'
import { useState, useEffect } from "react"
import { useAuth } from "@/lib/auth"
import { useToast } from "@/components/ui/toast"
import { getCustomers } from "@/lib/api-client"
import { exportToCSV, exportToExcel, exportToJSON, generateTimestampedFilename } from "@/lib/export-utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  IconDatabase,
  IconDownload,
  IconEye,
  IconEdit,
  IconTrash,
  IconDots,
  IconSearch,
  IconUser,
  IconPhone,
  IconBrandInstagram,
  IconTrendingUp,
  IconMail,
  IconFileExport,
  IconLoader2
} from "@tabler/icons-react"

interface Customer {
  id: string
  merchant_id: string
  name: string
  phone: string
  email?: string
  instagram?: string
  avatar_url?: string
  total_points: number
  games_played: number
  campaigns_participated: number
  last_play_date?: string
  first_play_date?: string
  created_at: string
  updated_at: string
}

const statusColors = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  vip: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
}

// Helper to calculate level from points (100 points per level)
const calculateLevel = (points: number): number => {
  return Math.floor(points / 100) + 1
}

// Helper to calculate status from points and last play date
const calculateStatus = (points: number, lastPlayDate?: string): "active" | "inactive" | "vip" => {
  if (points >= 2000) return "vip"

  if (!lastPlayDate) return "inactive"

  const lastPlay = new Date(lastPlayDate)
  const daysSinceLastPlay = Math.floor((Date.now() - lastPlay.getTime()) / (1000 * 60 * 60 * 24))

  return daysSinceLastPlay > 30 ? "inactive" : "active"
}

// Helper to get source from campaign participation
const getSource = (campaignsParticipated: number): string => {
  if (campaignsParticipated === 0) return "Direct Signup"
  return "QR Campaign"
}

// Helper to get days since last active
const getDaysSinceLastActive = (lastPlayDate?: string): number => {
  if (!lastPlayDate) return 999
  const date = new Date(lastPlayDate)
  const today = new Date()
  const diffTime = Math.abs(today.getTime() - date.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

export default function CustomerDataPage() {
  return (
    <AuthGuard>
      <CustomerDataPageContent />
    </AuthGuard>
  )
}

function CustomerDataPageContent() {
  const { user, getMerchantId } = useAuth()
  const { addToast } = useToast()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [exportFormat, setExportFormat] = useState<string>("csv")
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)

  // Load customers
  useEffect(() => {
    if (user && getMerchantId()) {
      loadCustomers()
    } else {
      setIsLoading(false)
    }
  }, [user, getMerchantId])

  const loadCustomers = async () => {
    try {
      setIsLoading(true)
      const response = await getCustomers(100, 0)

      // Handle both array and object responses
      // API returns {success: true, data: [...]} which gets unwrapped to [...]
      // But sometimes returns null or empty object {} when no data
      if (Array.isArray(response)) {
        setCustomers(response)
      } else if (response === null || (typeof response === 'object' && Object.keys(response).length === 0)) {
        // Null or empty object response - treat as empty array
        setCustomers([])
      } else {
        console.error('Invalid response format:', response)
        setCustomers([])
      }
    } catch (error) {
      console.error('Failed to load customers:', error)
      addToast({
        message: "Failed to load customer data",
        variant: "error",
        duration: 3000
      })
      setCustomers([])
    } finally {
      setIsLoading(false)
    }
  }

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.includes(searchTerm) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.instagram && customer.instagram.toLowerCase().includes(searchTerm.toLowerCase()))

    const status = calculateStatus(customer.total_points, customer.last_play_date)
    const matchesStatus = statusFilter === "all" || status === statusFilter

    return matchesSearch && matchesStatus
  })

  const handleExportData = () => {
    try {
      // Prepare export data with calculated fields
      const exportData = filteredCustomers.map(customer => ({
        customer_id: customer.id,
        merchant_id: customer.merchant_id,
        name: customer.name || 'Anonymous',
        phone: customer.phone,
        email: customer.email || '',
        instagram: customer.instagram || '',
        total_points: customer.total_points,
        level: calculateLevel(customer.total_points),
        games_played: customer.games_played,
        campaigns_participated: customer.campaigns_participated,
        status: calculateStatus(customer.total_points, customer.last_play_date),
        source: getSource(customer.campaigns_participated),
        last_play_date: customer.last_play_date || '',
        first_play_date: customer.first_play_date || '',
        joined_date: customer.created_at,
        last_updated: customer.updated_at
      }))

      // Generate filename with timestamp
      const filename = generateTimestampedFilename('customers', '')

      // Export based on selected format (functions add their own extensions)
      switch (exportFormat) {
        case 'csv':
          exportToCSV(exportData, filename)
          break
        case 'excel':
          exportToExcel(exportData, filename)
          break
        case 'json':
          exportToJSON(exportData, filename)
          break
        default:
          throw new Error(`Unsupported export format: ${exportFormat}`)
      }

      addToast({
        message: `Successfully exported ${filteredCustomers.length} customers as ${exportFormat.toUpperCase()}`,
        variant: "success",
        duration: 3000
      })
      setIsExportDialogOpen(false)
    } catch (error) {
      console.error('Export error:', error)
      addToast({
        message: error instanceof Error ? error.message : 'Failed to export data',
        variant: "error",
        duration: 3000
      })
    }
  }

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer)
    setIsDetailsModalOpen(true)
  }

  const handleEditCustomer = (customer: Customer) => {
    // TODO: Implement edit customer modal
    console.log('Edit customer:', customer)
  }

  const handleDeleteCustomer = (customer: Customer) => {
    if (confirm(`Are you sure you want to delete ${customer.name}?`)) {
      // TODO: Implement delete customer API call
      console.log('Delete customer:', customer)
      addToast({
        message: "Delete customer feature coming soon",
        variant: "default",
        duration: 2000
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
        <div className="flex items-center justify-center h-64">
          <IconLoader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Customer Data</h2>
        <div className="flex items-center space-x-2">
          <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <IconFileExport className="mr-2 h-4 w-4" />
                Export Data
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Export Customer Data</DialogTitle>
                <DialogDescription>
                  Choose the format for your customer data export.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="export-format">Export Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV File</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="json">JSON Data</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <p>Export will include:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>Customer contact information</li>
                    <li>Engagement metrics</li>
                    <li>Campaign participation history</li>
                    <li>Points and level data</li>
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleExportData}>
                  <IconDownload className="mr-2 h-4 w-4" />
                  Export Data
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <IconUser className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {customers.length.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Registered players
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Customers</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {customers.filter(c => calculateStatus(c.total_points, c.last_play_date) === "active").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Played in last 30 days
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">VIP Members</CardTitle>
            <IconDatabase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {customers.filter(c => calculateStatus(c.total_points, c.last_play_date) === "vip").length}
            </div>
            <p className="text-xs text-muted-foreground">
              2000+ points
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Points</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {customers.length > 0
                ? Math.round(customers.reduce((sum, c) => sum + c.total_points, 0) / customers.length)
                : 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Per customer
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <IconSearch className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Database</CardTitle>
          <CardDescription>
            View and manage all customer data collected through your campaigns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Engagement</TableHead>
                <TableHead>Points</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {searchTerm || statusFilter !== "all"
                      ? "No customers match your filters"
                      : "No customers yet. Create a QR campaign to start collecting customer data!"}
                  </TableCell>
                </TableRow>
              ) : null}
              {filteredCustomers.map((customer) => {
                const level = calculateLevel(customer.total_points)
                const status = calculateStatus(customer.total_points, customer.last_play_date)
                const source = getSource(customer.campaigns_participated)
                const daysSinceActive = getDaysSinceLastActive(customer.last_play_date)

                return (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{customer.name || "Anonymous"}</div>
                        <div className="text-sm text-muted-foreground">ID: {customer.id.slice(0, 8)}...</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <IconPhone className="w-3 h-3 text-muted-foreground" />
                          {customer.phone}
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-2">
                            <IconMail className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm">{customer.email}</span>
                          </div>
                        )}
                        {customer.instagram && (
                          <div className="flex items-center gap-2">
                            <IconBrandInstagram className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm">{customer.instagram}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[status]}>
                        {status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>Level {level}</div>
                        <div className="text-muted-foreground">{customer.games_played} games</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{customer.total_points.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">
                          {customer.campaigns_participated} campaigns
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm max-w-[150px] truncate">
                        {source}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {daysSinceActive >= 999 ? "Never" : `${daysSinceActive} days ago`}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <IconDots className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(customer)}>
                            <IconEye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {/*<DropdownMenuItem onClick={() => handleEditCustomer(customer)}>
                            <IconEdit className="mr-2 h-4 w-4" />
                            Edit Customer
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeleteCustomer(customer)}
                            className="text-red-600"
                          >
                            <IconTrash className="mr-2 h-4 w-4" />
                            Delete Customer
                          </DropdownMenuItem>*/}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Customer Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
            <DialogDescription>
              Full information about this customer
            </DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedCustomer.name || "Anonymous"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Customer ID</p>
                    <p className="font-mono text-xs">{selectedCustomer.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedCustomer.phone}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedCustomer.email || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Instagram</p>
                    <p className="font-medium">{selectedCustomer.instagram || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge className={statusColors[calculateStatus(selectedCustomer.total_points, selectedCustomer.last_play_date)]}>
                      {calculateStatus(selectedCustomer.total_points, selectedCustomer.last_play_date).toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Engagement Stats */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Engagement Statistics</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-muted-foreground text-xs">Total Points</p>
                    <p className="text-2xl font-bold">{selectedCustomer.total_points.toLocaleString()}</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-muted-foreground text-xs">Level</p>
                    <p className="text-2xl font-bold">{calculateLevel(selectedCustomer.total_points)}</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-muted-foreground text-xs">Games Played</p>
                    <p className="text-2xl font-bold">{selectedCustomer.games_played}</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-muted-foreground text-xs">Campaigns</p>
                    <p className="text-2xl font-bold">{selectedCustomer.campaigns_participated}</p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Important Dates</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Joined Date</p>
                    <p className="font-medium">{new Date(selectedCustomer.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">First Play</p>
                    <p className="font-medium">{selectedCustomer.first_play_date
                      ? new Date(selectedCustomer.first_play_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : "Never"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Play</p>
                    <p className="font-medium">{selectedCustomer.last_play_date
                      ? new Date(selectedCustomer.last_play_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : "Never"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Updated</p>
                    <p className="font-medium">{new Date(selectedCustomer.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</p>
                  </div>
                </div>
              </div>

              {/* Progress to Next Level */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Level Progress</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Level {calculateLevel(selectedCustomer.total_points)}</span>
                    <span className="text-muted-foreground">
                      {selectedCustomer.total_points % 100} / 100 points to Level {calculateLevel(selectedCustomer.total_points) + 1}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(selectedCustomer.total_points % 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Activity Summary */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Activity Summary</h3>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Source</span>
                    <span className="font-medium">{getSource(selectedCustomer.campaigns_participated)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Days Since Last Active</span>
                    <span className="font-medium">
                      {getDaysSinceLastActive(selectedCustomer.last_play_date) >= 999
                        ? "Never"
                        : `${getDaysSinceLastActive(selectedCustomer.last_play_date)} days`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
